import type { LegacyRedirectRoute, MovesTabKey, PortalProductRoutes, ProductKey } from '@hopdrive/feature-portal-products';

// The app's product route table: each product's route prefix, the Moves path builders, and the
// legacy product paths that redirect in. The product model reads it through
// `host.routing.routes`; the app's own pages import the builders directly. Every in-app product
// path is built here, so the URL shape lives in one file.

/** Each product's route prefix: its landing page, with its tabs beneath. */
export const PRODUCT_ROOT_PATH: Readonly<Record<ProductKey, string>> = {
  moves: '/moves',
  'guest-rides': '/guest-rides',
  'mobile-service': '/mobile-service',
  'parts-delivery': '/parts-delivery',
  appraisals: '/appraisals',
};

const MOVES_ROOT = PRODUCT_ROOT_PATH.moves;

export function productRootPath(product: ProductKey): string {
  return PRODUCT_ROOT_PATH[product];
}

/** The move detail page path (`/moves/:id`), optionally opening the move's chat on arrival
 * (`?openChat=true`). With no id there is no detail to link, so the path is the Moves root. */
export function movePath(id: number | string | null | undefined, options?: { openChat?: boolean }): string {
  if (id === null || id === undefined) return MOVES_ROOT;
  return `${MOVES_ROOT}/${id}${options?.openChat ? '?openChat=true' : ''}`;
}

/** The rideshare detail page path (`/moves/rideshares/:moveId`). */
export function ridesharePath(moveId: number | string): string {
  return `${MOVES_ROOT}/rideshares/${moveId}`;
}

/** The move planner page path (`/moves/plan-moves`). */
export function planMovesPath(): string {
  return `${MOVES_ROOT}/plan-moves`;
}

/** The CSV import page path (`/moves/import-moves`). */
export function importMovesPath(): string {
  return `${MOVES_ROOT}/import-moves`;
}

/** The path of a Moves tab by key (`/moves/<tab>`); the bare root for the Orders landing tab. */
export function movesTabPath(tab: MovesTabKey): string {
  return tab === 'orders' ? MOVES_ROOT : `${MOVES_ROOT}/${tab}`;
}

/**
 * The old product paths and the product-prefixed paths that replace them: the safety net for
 * bookmarks and external links, kept indefinitely. Every `/valet/*` path redirects to its `/moves`
 * counterpart, the old moves-list paths (`/valet/moves`, `/dashboard`) land on the Orders tab, and
 * the move detail (`/valet/moves/:id`) redirects to `/moves/:id`. Every entry lands on its
 * replacement; a Moves tab the customer does not have renders its restricted page there.
 */
export const LEGACY_PRODUCT_REDIRECTS: readonly LegacyRedirectRoute[] = [
  // The unprefixed legacy paths.
  { from: '/dashboard', to: '/moves' },
  { from: '/schedule', to: '/moves/schedule' },
  { from: '/rideshares', to: '/moves/rideshares' },
  { from: '/rideshares/:moveId', to: '/moves/rideshares/:moveId' },
  { from: '/plan-moves', to: '/moves/plan-moves' },
  { from: '/import-moves', to: '/moves/import-moves' },
  // The `/valet/*` paths, redirecting to their `/moves` counterparts.
  { from: '/valet', to: '/moves' },
  { from: '/valet/moves', to: '/moves' },
  { from: '/valet/moves/:id', to: '/moves/:id' },
  { from: '/valet/team', to: '/moves/team' },
  { from: '/valet/schedule', to: '/moves/schedule' },
  { from: '/valet/rideshares', to: '/moves/rideshares' },
  { from: '/valet/rideshares/:moveId', to: '/moves/rideshares/:moveId' },
  { from: '/valet/reports', to: '/moves/reports' },
  { from: '/valet/missing-ref', to: '/moves/missing-ref' },
  { from: '/valet/plan-moves', to: '/moves/plan-moves' },
  { from: '/valet/import-moves', to: '/moves/import-moves' },
  { from: '/parts-delivery/queue', to: '/parts-delivery' },
];

/** The route table the product model reads through `host.routing.routes`. */
export const PRODUCT_ROUTES: PortalProductRoutes = {
  productRoot: PRODUCT_ROOT_PATH,
  movePath,
  ridesharePath,
  planMovesPath,
  importMovesPath,
  movesTabPath,
  legacyRedirects: LEGACY_PRODUCT_REDIRECTS,
};
